import { convertPackageToCustomerPackage } from '#lib/helper/package.js';
import { packageStore } from '#lib/stores/package.js';
import type {
	CustomerPackage,
	CustomerPackageComposition,
	CustomerPackageUsage,
	Package,
	WidgetContent
} from '#lib/types/generated/index.js';
import { widgetContent } from '#lib/widget/core.js';
import { widgetAuthenticatedMember } from '#lib/widget/member.js';
import { widgetPrices, type WidgetBooking } from '#lib/widget/booking/booking.js';
import { DateTime } from 'luxon';
import { get, writable } from 'svelte/store';

export const widgetPackageForm = writable<(CustomerPackage & { code?: string }) | undefined>(
	undefined
);

export const widgetCustomerPackageToBeUsed = writable<CustomerPackage | undefined>(undefined);

export function filterWidgetContentByCustomerPackage(
	content: WidgetContent,
	customerPackage: CustomerPackage
): WidgetContent {
	const currentOffers = content.offers;
	const expiresIso = customerPackage.expiresOn?.toISODate() ?? null;
	const activeCompositions = customerPackage.compositions.filter((comp) => comp.quantity > 0);

	const filteredActivities = content.activities.filter((a) =>
		activeCompositions.some(
			(comp) =>
				comp.details.length === 0 ||
				comp.details.some(
					(d) =>
						d.activityId === a.id ||
						(d.offerId !== null &&
							d.offerId in currentOffers &&
							currentOffers[d.offerId].activityIds.includes(a.id))
				)
		)
	);

	const filteredOffers = Object.fromEntries(
		Object.entries(currentOffers)
			.filter(([offerId, o]) => {
				if (expiresIso && o.from && o.from > expiresIso) {
					return false;
				}
				return activeCompositions.some(
					(comp) =>
						comp.details.length === 0 ||
						comp.details.some(
							(d) =>
								d.offerId === offerId ||
								(d.activityId !== null && o.activityIds.includes(d.activityId))
						)
				);
			})
			.map(([offerId, o]) => {
				if (expiresIso && (!o.to || o.to > expiresIso)) {
					return [offerId, { ...o, to: expiresIso }];
				}
				return [offerId, o];
			})
	);

	const keptActivityIds = filteredActivities.map((a) => a.id);
	const filteredSessions = Object.fromEntries(
		Object.entries(content.sessions).filter(([_, s]) => keptActivityIds.includes(s.activityId))
	);

	return {
		...content,
		activities: filteredActivities,
		offers: filteredOffers,
		sessions: filteredSessions
	};
}

export function newCustomerPackage(pack: Package): void {
	const p = convertPackageToCustomerPackage(pack);
	p.customerId = get(widgetAuthenticatedMember)?.id ?? '';
	widgetPackageForm.set(p);
}

export function customerPackageCompositionAppliesToWidgetBooking(
	composition: CustomerPackageComposition,
	offerId: string,
	priceId: string | null
): boolean {
	if (composition.details.length === 0) return true;
	const content = get(widgetContent);
	const offer = offerId in content.offers ? content.offers[offerId] : undefined;
	const offerActivityIds = offer ? offer.activityIds : [];

	return composition.details.some((d) => {
		if (
			d.activityId !== null &&
			offerActivityIds.length > 0 &&
			!offerActivityIds.includes(d.activityId)
		) {
			return false;
		}
		if (d.offerId !== null && d.offerId !== offerId) return false;
		if (d.guestPriceId !== null && d.guestPriceId !== priceId) return false;
		return true;
	});
}

export function customerPackageCompositionCanApplyToThisWidgetPrice(
	comp: CustomerPackageComposition,
	booking: WidgetBooking | undefined,
	priceId: string | null = null
): boolean {
	return (
		comp.quantity > 0 &&
		!!booking?.offer &&
		customerPackageCompositionAppliesToWidgetBooking(comp, booking.offer.id, priceId)
	);
}

export function customerPackageCanApplyToThisWidgetBooking(
	cp: CustomerPackage,
	booking: WidgetBooking
): boolean {
	return cp.compositions.some((comp) =>
		customerPackageCompCanApplyToThisWidgetBooking(comp, booking)
	);
}

export function customerPackageCompCanApplyToThisWidgetBooking(
	comp: CustomerPackageComposition,
	booking: WidgetBooking
): boolean {
	return (
		customerPackageCompositionCanApplyToThisWidgetPrice(comp, booking, null) ||
		(get(widgetPrices)?.some(
			(p) =>
				booking.offer &&
				customerPackageCompositionAppliesToWidgetBooking(comp, booking.offer.id, p.id)
		) ??
			false)
	);
}

export function computeWidgetBookingPackageUsages(
	booking: WidgetBooking,
	customerPackages: CustomerPackage[]
): { booking: WidgetBooking; pricesUsedByPackages: Record<string, number> } {
	const packageUsages: CustomerPackageUsage[] = [];
	const pricesUsedByPackages: Record<string, number> = {};

	const requiredPrices = { ...booking.prices };
	if (Object.keys(requiredPrices).length === 0 && booking.offer) {
		requiredPrices[''] = 1;
	}
	const offerId = booking.offer?.id ?? '';

	for (const cp of customerPackages.filter((cp) =>
		customerPackageCanApplyToThisWidgetBooking(cp, booking)
	)) {
		let cpUsedInBooking = 0;
		const pkgMax = get(packageStore).find((p) => p.id === cp.packageId)?.maxQuantityPerBooking;
		const maxQuantityPerBooking = pkgMax && pkgMax > 0 ? pkgMax : Infinity;

		for (const comp of cp.compositions) {
			let compUsed = 0;

			for (const [priceId, qty] of Object.entries(requiredPrices)) {
				if (
					qty <= 0 ||
					!customerPackageCompositionAppliesToWidgetBooking(comp, offerId, priceId)
				) {
					continue;
				}

				const amountToUse = Math.min(
					comp.quantity - compUsed, // Available on composition
					maxQuantityPerBooking - cpUsedInBooking, // Available on package for this booking
					qty // Quantity needed
				);

				if (amountToUse <= 0) break;

				compUsed += amountToUse;
				cpUsedInBooking += amountToUse;
				requiredPrices[priceId] -= amountToUse;
				pricesUsedByPackages[priceId] = (pricesUsedByPackages[priceId] ?? 0) + amountToUse;
			}

			if (compUsed > 0) {
				packageUsages.push({
					id: '',
					customerPackageId: cp.id,
					compositionId: comp.id,
					quantity: compUsed,
					offerId: null,
					guestPriceId: null,
					bookingId: null,
					usedAt: DateTime.now()
				});
			}
			if (cpUsedInBooking >= maxQuantityPerBooking) break;
		}
	}

	return {
		booking: { ...booking, packageUsages },
		pricesUsedByPackages
	};
}
